import { useCallback } from 'react';
import type { CardVideoUploadPhase } from './useCardVideoUpload';

/**
 * Memoized onReady/onFailed/onProcessing for useCardVideoAssetPoll. Stable identities
 * matter here: an unmemoized callback would make useAttachedAssetResync's effect
 * (which depends on them) re-run on every unrelated state update in the parent hook —
 * re-running it while attachedExternalId is still unset calls stopPoll(), which would
 * cancel a poll confirmMetadata just started.
 */
export function useCardVideoUploadPhaseCallbacks(
  setPhase: (phase: CardVideoUploadPhase) => void,
  setErrorMessage: (message: string | null) => void,
) {
  const onReady = useCallback(() => {
    setPhase('idle');
    setErrorMessage(null);
  }, [setPhase, setErrorMessage]);
  const onFailed = useCallback(
    (message: string) => {
      setPhase('error');
      setErrorMessage(message);
    },
    [setPhase, setErrorMessage],
  );
  const onProcessing = useCallback(() => setPhase('transcoding'), [setPhase]);

  return { onReady, onFailed, onProcessing };
}
